import React, {
    ReactNode,
    createContext,
    isValidElement,
    useContext,
    useEffect,
    useRef,
    useState,
} from 'react';
import { Danger, TickCircle, InfoCircle, CloseCircle } from '@/components/icons';
import { AppDialogLayout } from '@/components/system';
import { DialogChromeProvider } from '@/components/system/dialog-chrome';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

export interface ModalFooterConfig {
    closeText?: string;
    acceptText?: string;
    onClose?: () => void;
    onAccept?: (close: () => void) => void;
    closeVariant?: 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link';
    acceptVariant?: 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link';
}

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl';
export type ModalVariant = 'default' | 'success' | 'warning' | 'destructive' | 'info';

export interface ModalOptions {
    title?: ReactNode;
    description?: ReactNode;
    /**
     * Replaces the glyph the `variant` would put beside the title. Pass a
     * self-contained visual (an avatar, a thumbnail) — it renders without the
     * variant's tinted plate behind it.
     */
    icon?: ReactNode;
    content: ReactNode;
    footer?: ModalFooterConfig | ReactNode;
    className?: string;
    bodyClassName?: string;
    size?: ModalSize;
    variant?: ModalVariant;
    showCloseButton?: boolean;
}

const WIZARD_BODY_CLASS = 'overflow-hidden flex flex-col min-h-0 p-0';

interface ModalContextType {
    openModal: (options: ModalOptions) => void;
    openWizardModal: (options: ModalOptions) => void;
    closeModal: () => void;
    isOpen: boolean;
}

const ModalContext = createContext<ModalContextType | undefined>(undefined);

export const ModalProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [modalData, setModalData] = useState<ModalOptions | null>(null);
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        return () => {
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
        };
    }, []);

    const MODAL_SIZE_CLASSES: Record<ModalSize, string> = {
        sm: 'max-w-sm',
        md: 'max-w-lg',
        lg: 'max-w-3xl',
        xl: 'max-w-5xl',
    };

    const openModal = React.useCallback((options: ModalOptions) => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
        setModalData(options);
        setIsOpen(true);
    }, []);

    const openWizardModal = React.useCallback(
        (options: ModalOptions) => {
            openModal({
                ...options,
                bodyClassName: WIZARD_BODY_CLASS,
            });
        },
        [openModal],
    );

    const closeModal = React.useCallback(() => {
        setIsOpen(false);
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => {
            setModalData(null);
            timeoutRef.current = null;
        }, 300);
    }, []);

    const handleOpenChange = React.useCallback(
        (open: boolean) => {
            if (!open) {
                closeModal();
            } else {
                setIsOpen(true);
            }
        },
        [closeModal],
    );

    const getVariantConfig = (variant: ModalVariant = 'default') => {
        switch (variant) {
            case 'destructive':
                return {
                    icon: <CloseCircle className="size-6 text-destructive" />,
                    defaultAcceptVariant: 'destructive' as const,
                    tone: 'destructive' as const,
                };
            case 'success':
                return {
                    icon: <TickCircle className="size-6 text-success" />,
                    defaultAcceptVariant: 'default' as const,
                    tone: 'success' as const,
                };
            case 'warning':
                return {
                    icon: <Danger className="size-6 text-warning" />,
                    defaultAcceptVariant: 'default' as const,
                    tone: 'warning' as const,
                };
            case 'info':
                return {
                    icon: <InfoCircle className="size-6 text-info" />,
                    defaultAcceptVariant: 'default' as const,
                    tone: 'info' as const,
                };
            default:
                return {
                    icon: null,
                    defaultAcceptVariant: 'default' as const,
                    tone: 'default' as const,
                };
        }
    };

    const renderFooter = () => {
        if (!modalData?.footer) return null;

        if (isValidElement(modalData.footer)) {
            return <DialogFooter className="sm:space-x-4">{modalData.footer}</DialogFooter>;
        }

        if (typeof modalData.footer !== 'object') {
            return <DialogFooter className="sm:space-x-4">{modalData.footer}</DialogFooter>;
        }

        const footerConfig = modalData.footer as ModalFooterConfig;
        const variantConfig = getVariantConfig(modalData.variant);

        return (
            <DialogFooter className="gap-2 sm:space-x-0">
                <Button
                    variant={footerConfig.closeVariant || 'outline'}
                    onClick={() => {
                        if (footerConfig.onClose) footerConfig.onClose();
                        closeModal();
                    }}
                >
                    {footerConfig.closeText || 'Cancelar'}
                </Button>
                <Button
                    variant={footerConfig.acceptVariant || variantConfig.defaultAcceptVariant}
                    onClick={() => {
                        if (footerConfig.onAccept) {
                            footerConfig.onAccept(closeModal);
                        } else {
                            closeModal();
                        }
                    }}
                >
                    {footerConfig.acceptText || 'Confirmar'}
                </Button>
            </DialogFooter>
        );
    };

    const variantConfig = modalData ? getVariantConfig(modalData.variant) : null;

    // With a title, description or footer the shell owns the dialog chrome.
    // Without any of them the content does (wizard-style modals), and it renders
    // its own `AppDialogLayout` — which picks the close up from the context below.
    const ownsChrome = Boolean(
        modalData?.title != null || modalData?.description != null || modalData?.footer != null,
    );

    // Radix focuses the first tabbable node, which is now the header close. Send
    // focus to the body instead, so a dialog opens on its content (a search field,
    // the first option) exactly as it did when the close sat in the corner.
    const handleOpenAutoFocus = React.useCallback((event: Event) => {
        const content = event.currentTarget as HTMLElement | null;
        const body = content?.querySelector<HTMLElement>('[data-dialog-body]');
        if (!body) return;

        event.preventDefault();
        const focusable = body.querySelector<HTMLElement>(
            'input:not([disabled]), textarea:not([disabled]), select:not([disabled]), button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])',
        );
        (focusable ?? content)?.focus();
    }, []);

    const closeAction = React.useMemo(
        () =>
            modalData?.showCloseButton === false
                ? undefined
                : { label: 'Fechar', onClick: closeModal },
        [modalData?.showCloseButton, closeModal],
    );

    return (
        <ModalContext.Provider
            value={{
                openModal,
                openWizardModal,
                closeModal,
                isOpen,
            }}
        >
            {children}
            <Dialog open={isOpen} onOpenChange={handleOpenChange}>
                <DialogContent
                    {...(!modalData?.description ? { 'aria-describedby': undefined } : {})}
                    onOpenAutoFocus={handleOpenAutoFocus}
                    // The close lives in the layout's header row, not pinned to the
                    // corner, so it lines up with the title and any back control.
                    showCloseButton={false}
                    className={cn(
                        'flex max-h-[calc(100vh-2rem)] min-h-0 flex-col gap-0 overflow-hidden border-border/80 p-0 shadow-elevation-2 outline-none',
                        modalData?.size ? MODAL_SIZE_CLASSES[modalData.size] : 'max-w-lg',
                        modalData?.className,
                    )}
                >
                    <DialogChromeProvider close={closeAction}>
                        {ownsChrome ? (
                            <AppDialogLayout
                                title={modalData?.title}
                                description={modalData?.description}
                                icon={modalData?.icon ?? variantConfig?.icon}
                                tone={modalData?.icon ? 'none' : variantConfig?.tone}
                                bodyClassName={modalData?.bodyClassName}
                                footer={renderFooter()}
                            >
                                {modalData?.content}
                            </AppDialogLayout>
                        ) : (
                            // Chrome-less options mean the content brings its own
                            // `AppDialogLayout`; wrapping it again would nest two
                            // headers, so hand the dialog straight to it.
                            modalData?.content
                        )}
                    </DialogChromeProvider>
                </DialogContent>
            </Dialog>
        </ModalContext.Provider>
    );
};

export const useModalContext = () => {
    const context = useContext(ModalContext);
    if (!context) {
        throw new Error('useModalContext must be used within a ModalProvider');
    }
    return context;
};
